import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { ResourceErrorType } from '@lb/components/resource-error/resource-error.component';
import { toResourceErrorType } from '@lb/components/resource-error/resource-error.util';
import { GradingTemplateCategoryType, IGradingTemplateDetail, IGradingTemplateSurveyDetail } from '@lb/api/core/interfaces/igrading-template.interface';
import { CoreGradingTemplateService } from '@lb/api/core/services/core-grading-template.service';
import { CoreCourseCatalogService } from '@lb/api/core/services/core-course-catalog.service';
import { ILessonDetail, ILessonListItem, ISaveSurveyResponseRequest, ISurveyResponseSummary } from '@lb/api/mylb/interfaces';
import { ISemesterDetail } from '@lb/api/mylb/interfaces/enrollment/ienrollment-detail.interface';
import { EnrollmentDetailService, LessonDetailService, LessonV2Service, SurveyResponseService } from '@lb/api/mylb/services';
import { CoreGradeScaleService } from '@lb/api/core/services/core-grade-scale.service';
import { ICoreGrade } from '@lb/api/core/interfaces';
import { IMDDropDownOption } from '@melodic-ui/components';
import { AccountStateService } from '@lb/state/account-state.service';
import { APP_CONFIG, IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';
import { UserStateService } from '@lb/state/user-state.service';
import { AnalyticsService, EnrollmentChangeTrackerService } from '@lb/services';
import { take, tap } from 'rxjs';
import { LessonStatus } from '@lb/api/mylb/enums';
import { getLessonTypeVariant } from '@lb/components/lessons/utils/lesson-type-colors.util';
import { isSurveyAvailable, SURVEY_RESPONDENT_INSTRUCTOR, SURVEY_RESPONDENT_LEARNER } from '@lb/components/lessons/utils/survey-window.util';
import { findSurveyResponse, isSameSurveyResponseKey } from '@lb/components/lessons/utils/survey-response.util';

@Injectable()
export class LessonDetailStateService {
	private readonly lessonService = inject(LessonV2Service);
	private readonly lessonDetailService = inject(LessonDetailService);
	private readonly accountStateService = inject(AccountStateService);
	private readonly userStateService = inject(UserStateService);
	private readonly gradingTemplateService = inject(CoreGradingTemplateService);
	private readonly courseCatalogService = inject(CoreCourseCatalogService);
	private readonly surveyResponseService = inject(SurveyResponseService);
	private readonly gradeScaleService = inject(CoreGradeScaleService);
	private readonly enrollmentDetailService = inject(EnrollmentDetailService);
	private readonly analytics = inject(AnalyticsService);
	private readonly enrollmentChangeTracker = inject(EnrollmentChangeTrackerService);
	private readonly appSettings = inject<IAppSettings>(APP_CONFIG);

	isAdmin = this.accountStateService.isAdmin;

	lessonID = signal<number>(-1);

	private readonly _lessonLoading = signal<boolean>(false);
	private readonly _lessonLoaded = signal<boolean>(false);
	private readonly _lessonError = signal<boolean>(false);
	private readonly _lessonErrorType = signal<ResourceErrorType | null>(null);
	/** Typed reason the lesson failed to load (404 / 403 / malformed id / other), or null when healthy. */
	public readonly lessonErrorType = this._lessonErrorType.asReadonly();
	private readonly _lesson = signal<ILessonDetail | null>(null);
	private readonly _gradingTemplateError = signal<boolean>(false);
	private readonly _gradingTemplate = signal<IGradingTemplateDetail | null>(null);
	private readonly _surveyResponses = signal<ISurveyResponseSummary[]>([]);
	private readonly _enrollmentDetailLoading = signal<boolean>(false);
	private readonly _enrollmentDetailLoaded = signal<boolean>(false);
	private readonly _enrollmentDetailError = signal<boolean>(false);
	private readonly _gradeScaleLoading = signal<boolean>(false);
	private readonly _gradeScaleLoaded = signal<boolean>(false);
	private readonly _gradeScaleError = signal<boolean>(false);
	private readonly _objectivesLoading = signal<boolean>(false);
	private readonly _objectivesLoaded = signal<boolean>(false);
	private readonly _objectivesError = signal<boolean>(false);

	relatedLessonsLoading = signal<boolean>(false);
	relatedLessonsLoaded = signal<boolean>(false);
	relatedLessonsError = signal<boolean>(false);

	relatedLessons = signal<ILessonListItem[]>([]);

	// Enrollment Detail / Semester
	private readonly _currentSemester = signal<ISemesterDetail | null>(null);
	currentSemester = computed(() => this._currentSemester());

	navigationSource = signal<{ type: 'instructor' | 'learner' | 'enrollment'; id: number } | null>(null);
	/** The lesson ID currently focused via scroll or hover in the lesson list, or the open lesson on detail view. */
	activeLessonID = signal<number | null>(null);
	/** The lesson ID highlighted in the timeline — only updates on explicit user actions (initial load, click), not scroll. */
	timelineActiveLessonID = signal<number | null>(null);
	/** Set to a lesson ID to programmatically scroll the lesson list to that card (e.g. from a timeline sliver click). */
	programmaticScrollRequest = signal<number | null>(null);

	// Safe nullable accessor — use in templates and computed signals that run before loading completes
	lesson = computed(() => this._lesson());

	// Non-null accessor — use only in code paths guarded by loaded() check
	lessonRequired = computed(() => {
		const lesson = this._lesson();

		if (!lesson) {
			throw new Error('Lesson not loaded');
		}

		return lesson;
	});

	isSaving = signal(false);
	loading = computed(() => this._lessonLoading());
	loaded = computed(() => this._lessonLoaded());
	hasError = computed(
		() =>
			this._lessonError() ||
			this.relatedLessonsError() ||
			this._gradingTemplateError() ||
			this._enrollmentDetailError() ||
			this._gradeScaleError() ||
			this._objectivesError()
	);
	detailContentReady = computed(
		() =>
			this._lessonLoaded() &&
			this.relatedLessonsLoaded() &&
			this.gradingTemplateLoaded() &&
			this._enrollmentDetailLoaded() &&
			this._gradeScaleLoaded() &&
			this._objectivesLoaded()
	);

	semesterLessonType = computed(() => this.lesson()?.semesterLessonTypeID);
	isAbsent = computed(() => this.lesson()?.attendanceStatusID === 2);
	isAwaitingPayment = computed(() => this.lesson()?.lessonStatusID === LessonStatus.AwaitingPayment);
	isPendingReschedule = computed(() => this.lesson()?.lessonStatusID === LessonStatus.PendingReschedule);
	isInstructional = computed(() => this.lesson()?.lessonTypeID === 2);
	isMastery = computed(() => {
		const lesson = this.lesson();
		return lesson != null && getLessonTypeVariant(lesson.lessonTypeID) === 'mastery';
	});
	/**
	 * Chirp room lessons. Keyed off the lesson type rather than the room's grading template or
	 * attendance mode: the feedback fields are suppressed because a room lesson is taught to a group
	 * with no single learner to write up, which is a property of the lesson type itself.
	 */
	isChirpRoom = computed(() => {
		const lesson = this.lesson();
		return lesson != null && getLessonTypeVariant(lesson.lessonTypeID) === 'chirp';
	});
	isFinalSemester = computed(() => this.semesterLessonType() === 5);

	/**
	 * External feedback survey link (config-driven, e.g. a Google Form), offered on the final lesson
	 * only. Outside the surveys model on purpose: a plain link with no parameters and no completion
	 * tracking. Null when unconfigured or on any other lesson — the button simply doesn't render.
	 */
	externalFeedbackSurveyUrl = computed<string | null>(() => {
		if (!this.isFinalSemester()) return null;
		return this.appSettings.externalLinks?.feedbackSurveyUrl?.trim() || null;
	});
	showDetails = computed(() => {
		if (this.isAbsent() || this.isAwaitingPayment() || this.isPendingReschedule()) {
			return false;
		}

		return true;
	});

	// Grading Template Computed Properties
	gradingTemplateLoading = signal<boolean>(false);
	gradingTemplateLoaded = signal<boolean>(false);
	gradingTemplate = computed(() => this._gradingTemplate());
	// True once the grading template has finished loading AND a template is actually assigned.
	// Instructional lessons whose program has no grading template should be treated like any
	// other non-graded lesson type and fall back to the summary-only view.
	hasGradingTemplate = computed(() => this.gradingTemplateLoaded() && this._gradingTemplate() !== null);
	gradingTemplateCategories = computed(() => this._gradingTemplate()?.categories ?? []);
	gradingTemplateSurveys = computed(() => this._gradingTemplate()?.surveys ?? []);
	nonProjectCategories = computed(() => this.gradingTemplateCategories().filter((c) => c.categoryType !== GradingTemplateCategoryType.Project));
	projectCategory = computed(() => this.gradingTemplateCategories().find((c) => c.categoryType === GradingTemplateCategoryType.Project) ?? null);

	// Grade Scale
	grades = signal<ICoreGrade[]>([]);
	gradeOptions = computed<IMDDropDownOption[]>(() => [
		...this.grades().map((g) => ({ value: g.gradeID, label: g.gradeName })),
		{ value: null, label: 'No Grade' }
	]);

	// Objectives
	objectiveOptions = signal<IMDDropDownOption[]>([]);
	// True when the lesson's program has a course catalog (and version) assigned. Objectives are
	// keyed off the course catalog version, so without one there's no objectives concept to show at
	// all. This is distinct from a catalog being assigned but simply having zero objectives
	// configured - that case should still show the (empty) picker as a signal to the instructor that
	// the catalog may need attention, rather than hiding the component outright.
	hasCourseCatalog = computed(() => !!this.lesson()?.courseCatalogID && !!this.lesson()?.courseCatalogVersionID);

	/**
	 * How many planned-curriculum objectives this lesson's semester resolves to, or null until loaded.
	 * Only fetched when a survey actually contains a CourseObjectives question.
	 */
	private readonly _plannedObjectiveCount = signal<number | null>(null);
	/** Guards the objectives fetch so it runs once per lesson rather than on every signal change. */
	private _plannedObjectivesForLessonID: number | null = null;

	constructor() {
		// A survey with a CourseObjectives question is only offerable when the enrollment's planned
		// curriculum has objectives to expand it into — resolve the count as soon as the lesson and
		// its surveys are known so the survey rows don't have to.
		effect(() => {
			const lesson = this.lesson();
			const surveys = this.gradingTemplateSurveys();
			if (!lesson?.enrollmentID || !lesson?.lessonID) return;
			if (!surveys.some((s) => s.hasCourseObjectivesQuestion)) return;
			if (this._plannedObjectivesForLessonID === lesson.lessonID) return;

			this._plannedObjectivesForLessonID = lesson.lessonID;
			this._plannedObjectiveCount.set(null);
			this.surveyResponseService
				.getPlannedObjectives(lesson.enrollmentID, lesson.lessonID)
				.pipe(take(1))
				.subscribe({
					next: (objectives) => this._plannedObjectiveCount.set(objectives.length),
					// Fail closed: hiding the survey beats offering one that cannot render its questions.
					error: () => this._plannedObjectiveCount.set(0)
				});
		});
	}

	/**
	 * A survey with a CourseObjectives question is hidden when the planned curriculum resolves to zero
	 * objectives (or hasn't resolved yet). An already-submitted response stays visible regardless — the
	 * learner may always read back what they answered.
	 */
	private _isSurveyOfferable(survey: IGradingTemplateSurveyDetail): boolean {
		if (!survey.hasCourseObjectivesQuestion) return true;
		if (this.isSurveySubmitted(survey.surveyID)) return true;
		return (this._plannedObjectiveCount() ?? 0) > 0;
	}

	activeSurvey = computed(() => {
		const semesterLessonType = this.semesterLessonType();
		if (!semesterLessonType) {
			return null;
		}

		return this.gradingTemplateSurveys().find((s) => s.semesterLessonTypeID === semesterLessonType && this._isSurveyOfferable(s)) ?? null;
	});
	hasSurvey = computed(() => this.activeSurvey() !== null);

	/** All learner-facing surveys for the current lesson (respondentType === 1 and matching semesterLessonTypeID). */
	activeLearnerSurveys = computed(() => {
		const semesterLessonType = this.semesterLessonType();
		if (!semesterLessonType) return [];
		return this.gradingTemplateSurveys().filter(
			(s) => s.semesterLessonTypeID === semesterLessonType && s.respondentType === SURVEY_RESPONDENT_LEARNER && this._isSurveyOfferable(s)
		);
	});

	/** All instructor-facing surveys for the current lesson (respondentType === 2 and matching semesterLessonTypeID). */
	activeInstructorSurveys = computed(() => {
		const semesterLessonType = this.semesterLessonType();
		if (!semesterLessonType) return [];
		return this.gradingTemplateSurveys().filter(
			(s) => s.semesterLessonTypeID === semesterLessonType && s.respondentType === SURVEY_RESPONDENT_INSTRUCTOR && this._isSurveyOfferable(s)
		);
	});

	surveyResponses = computed(() => this._surveyResponses());

	/**
	 * Whether a survey on this lesson may be answered yet — its own availableDaysBefore counted back
	 * from the lesson date. See survey-window.util for the rule.
	 */
	surveyAvailable(survey: IGradingTemplateSurveyDetail): boolean {
		const lesson = this.lesson();
		return lesson ? isSurveyAvailable(lesson, survey) : false;
	}

	// Survey definitions are shared across semesters, so completion is matched on the survey's scope —
	// for First / Mid / Final that is this lesson's semester, not its ID (see findSurveyResponse).
	getSurveyResponseSummary(surveyID: number): ISurveyResponseSummary | null {
		const survey = this.gradingTemplateSurveys().find((s) => s.surveyID === surveyID);
		if (!survey) return null;
		return findSurveyResponse(this._surveyResponses(), survey, { lessonID: this.lessonID(), semesterID: this.lesson()?.semesterID });
	}

	isSurveySubmitted(surveyID: number): boolean {
		return this.getSurveyResponseSummary(surveyID)?.isSubmitted === true;
	}

	experience = computed<'instructor' | 'learner' | null>(() => {
		const user = this.accountStateService.user();
		if (!user) {
			return null;
		}

		/// Instructors and admins use 'instructor' context, learners use 'learner' context
		if (this.accountStateService.isAdmin() || this.accountStateService.isInstructor()) {
			return 'instructor';
		}

		return 'learner';
	});

	// Determines whose enrollments we load and which side the user dropdown shows.
	// For admin: derived from route (instructor page → 'instructor', learner page → 'learner').
	// For non-admin: same as experience.
	perspective = computed<'instructor' | 'learner'>(() => {
		const source = this.navigationSource();

		if (this.accountStateService.isAdmin() && source) {
			return source.type === 'learner' ? 'learner' : 'instructor';
		}

		if (this.accountStateService.isInstructor()) return 'instructor';
		return 'learner';
	});

	// The userID whose enrollments we load.
	// For admin: uses the route's :id param (instructor or learner page), or falls back to the lesson's instructor.
	// For non-admin: the logged-in user's own ID.
	userID = computed(() => {
		const source = this.navigationSource();

		if (this.accountStateService.isAdmin()) {
			if (source && (source.type === 'instructor' || source.type === 'learner')) {
				return source.id;
			}
			// Enrollment route or no source — fall back to the lesson's instructor
			return this._lesson()?.instructorUserID;
		}

		return this.accountStateService.userID();
	});

	/**
	 * Reload after a write to this lesson, flagging its enrollment as changed so pages that cache the
	 * enrollment (e.g. the admin enrollment overview and lesson list) reload it instead of showing stale data.
	 */
	reloadAfterChange(lessonID: number, bustCache?: boolean): void {
		this.enrollmentChangeTracker.markChanged(this._lesson()?.enrollmentID);
		this.loadLesson(lessonID, bustCache);
	}

	loadLesson(lessonID: number, bustCache?: boolean): void {
		this.lessonID.set(lessonID);

		// Guard against malformed ids before hitting the API (e.g. /lessons/abc -> NaN, or 0).
		if (!lessonID || isNaN(lessonID)) {
			this._lessonLoading.set(false);
			this._lessonError.set(true);
			this._lessonErrorType.set('invalid');
			return;
		}

		this._lessonLoading.set(true);
		this._lessonLoaded.set(false);
		this._lessonError.set(false);
		this._lessonErrorType.set(null);
		this.relatedLessonsLoaded.set(false);
		this.relatedLessonsError.set(false);
		this.gradingTemplateLoaded.set(false);
		this._gradingTemplateError.set(false);
		this._enrollmentDetailLoaded.set(false);
		this._enrollmentDetailError.set(false);
		this._gradeScaleLoaded.set(false);
		this._gradeScaleError.set(false);
		this._objectivesLoaded.set(false);
		this._objectivesError.set(false);

		this.lessonDetailService
			.getLesson(lessonID)
			.pipe(take(1))
			.subscribe({
				next: (lesson) => {
					this._lesson.set(lesson);
					this._lessonLoaded.set(true);
					this._lessonLoading.set(false);
					this._lessonError.set(false);

					// Tag the lesson context on the recording for filtering sessions in Clarity.
					this.analytics.setContext({
						lessonId: lesson.lessonID,
						enrollmentId: lesson.enrollmentID,
						semesterId: lesson.semesterID,
						learnerId: lesson.learnerUserID,
						instructorId: lesson.instructorUserID,
						programId: lesson.programID
					});

					this.loadRelatedLessons(lesson.enrollmentID).subscribe();
					this.loadEnrollmentDetail(lesson.enrollmentID, lesson.semesterID, bustCache);

					this.loadGradingTemplate(lesson.program.gradingTemplateID);
					this.loadGradeScale(lesson.program.gradeScaleID);
					this.loadObjectives(lesson);

					const users = [lesson.learnerUserID];
					if (lesson.instructorUserID) {
						users.push(lesson.instructorUserID);
					}

					this.userStateService.loadUserSummaries(users);
				},
				error: (error) => {
					console.error('Error loading lesson details:', error);
					this._lessonLoading.set(false);
					this._lessonError.set(true);
					this._lessonErrorType.set(toResourceErrorType(error));
				}
			});
	}

	loadRelatedLessons(enrollmentID: number) {
		this.relatedLessonsLoading.set(true);

		return this.lessonService.getLessons({ enrollmentID, page: 1, pageSize: 200, sortBy: 'lessonDate', sortOrder: 'asc' }).pipe(
			take(1),
			tap({
				next: (response) => {
					this.relatedLessons.set(response.items);
					this.relatedLessonsLoaded.set(true);
					this.relatedLessonsLoading.set(false);
					this.relatedLessonsError.set(false);
				},
				error: (error) => {
					console.error('Error loading related lessons:', error);
					this.relatedLessonsLoaded.set(true);
					this.relatedLessonsLoading.set(false);
					this.relatedLessonsError.set(true);
				}
			})
		);
	}

	loadEnrollmentDetail(enrollmentID: number, semesterID?: number, bustCache?: boolean): void {
		this._enrollmentDetailLoading.set(true);
		this._enrollmentDetailLoaded.set(false);
		this._enrollmentDetailError.set(false);

		this.enrollmentDetailService
			.getEnrollmentDetail(enrollmentID, bustCache)
			.pipe(take(1))
			.subscribe({
				next: (detail) => {
					const semester = detail.semesters?.find((s) => s.semesterID === semesterID) ?? null;
					this._currentSemester.set(semester); // Hydrate survey responses from the lesson detail payload
					this._surveyResponses.set(detail.surveyResponses ?? []);
					this._enrollmentDetailLoaded.set(true);
					this._enrollmentDetailLoading.set(false);
				},
				error: (error) => {
					console.error('Error loading enrollment detail:', error);
					this._currentSemester.set(null);
					this._enrollmentDetailError.set(true);
					this._enrollmentDetailLoaded.set(true);
					this._enrollmentDetailLoading.set(false);
				}
			});
	}

	submitSurveyResponse(request: ISaveSurveyResponseRequest): void {
		this.surveyResponseService
			.saveSurveyResponse(request)
			.pipe(take(1))
			.subscribe({
				next: (summary) => {
					this.enrollmentChangeTracker.markChanged(this._lesson()?.enrollmentID);
					this.upsertSurveyResponseSummary(summary);
				},
				error: (err) => console.error('Error submitting survey response:', err)
			});
	}

	upsertSurveyResponseSummary(summary: ISurveyResponseSummary): void {
		const current = this._surveyResponses();
		const existing = current.findIndex((r) => isSameSurveyResponseKey(r, summary));
		if (existing >= 0) {
			const updated = [...current];
			updated[existing] = summary;
			this._surveyResponses.set(updated);
		} else {
			this._surveyResponses.set([...current, summary]);
		}
	}

	loadGradingTemplate(templateID: number) {
		this.gradingTemplateLoading.set(true);
		this.gradingTemplateLoaded.set(false);
		this._gradingTemplateError.set(false);

		// Enrollments without a grading template (e.g. non-instructional-only programs) have no
		// template ID. Treat this as "no template" rather than an error so the summary-only detail
		// view can render instead of the page-level error state.
		if (!templateID) {
			this._gradingTemplate.set(null);
			this.gradingTemplateLoading.set(false);
			this.gradingTemplateLoaded.set(true);
			return;
		}

		this.gradingTemplateService
			.getGradingTemplateByID(templateID)
			.pipe(take(1))
			.subscribe({
				next: (t) => {
					this._gradingTemplate.set(t);
					this.gradingTemplateLoading.set(false);
					this.gradingTemplateLoaded.set(true);
				},
				error: (error) => {
					console.error('Error loading grading template:', error);
					this.gradingTemplateLoading.set(false);
					this.gradingTemplateLoaded.set(true);
					this._gradingTemplateError.set(true);
				}
			});
	}

	loadGradeScale(gradeScaleID: number): void {
		this._gradeScaleLoading.set(true);
		this._gradeScaleLoaded.set(false);
		this._gradeScaleError.set(false);

		// No grade scale (programs without a grading template) — degrade gracefully so the
		// summary-only detail view renders. Grades aren't needed without gradable categories.
		if (!gradeScaleID) {
			this.grades.set([]);
			this._gradeScaleLoaded.set(true);
			this._gradeScaleLoading.set(false);
			return;
		}

		// Fetched by ID rather than filtered out of the full list: a program can sit on a scale that has
		// since been deactivated, and the by-ID read returns it regardless.
		this.gradeScaleService
			.getGradeScaleByID(gradeScaleID)
			.pipe(take(1))
			.subscribe({
				next: (scale) => {
					this.grades.set(scale.grades ?? []);
					this._gradeScaleLoaded.set(true);
					this._gradeScaleLoading.set(false);
				},
				error: (error) => {
					console.error('Error loading grade scale:', error);
					this._gradeScaleError.set(true);
					this._gradeScaleLoaded.set(true);
					this._gradeScaleLoading.set(false);
				}
			});
	}

	loadObjectives(lesson: ILessonDetail): void {
		this._objectivesLoading.set(true);
		this._objectivesLoaded.set(false);
		this._objectivesError.set(false);

		if (!lesson.courseCatalogID || !lesson.courseCatalogVersionID) {
			this.objectiveOptions.set([]);
			this._objectivesLoaded.set(true);
			this._objectivesLoading.set(false);
			return;
		}

		this.courseCatalogService
			.getVersionObjectives(lesson.courseCatalogID, lesson.courseCatalogVersionID, {
				languageId: lesson.languageID,
				languageLevelId: lesson.languageLevelID ?? undefined
			})
			.pipe(take(1))
			.subscribe({
				next: (objectives) => {
					this.objectiveOptions.set(objectives.map((o) => ({ value: o.courseObjectiveID, label: o.objectiveName })));
					this._objectivesLoaded.set(true);
					this._objectivesLoading.set(false);
				},
				error: (error) => {
					console.error('Error loading objectives:', error);
					this._objectivesError.set(true);
					this._objectivesLoaded.set(true);
					this._objectivesLoading.set(false);
				}
			});
	}
}
